import dayjs from 'dayjs';
import { MOMENT_DATE_TIME_MASK, WTS_MOMENT_SHORT_DATE_MASK } from 'src/constants/dayjs';
import Utils from 'src/utils';
import { I18n } from 'src/translations/i18n';
import { EntityDataTypes, knownEntityTypes } from 'src/interfaces/modular/entityInfoTypes';
import { precisionRound } from 'src/utils/numbers';
import PersonContracts from 'src/helpers/modular/mainReport/PersonContracts';
export class MainReport {
    report;
    rawReport;
    workDaysData;
    levels;
    byDaysBase;
    currentPersonContracts;
    constructor(data) {
        this.report = [];
        this.rawReport = data.rawReport;
        this.workDaysData = data.workDaysData;
        this.levels = data.levels;
        this.byDaysBase = {};
        this.currentPersonContracts = new PersonContracts([]);
        this.createReport();
    }
    createReport() {
        this.parsePeriodTotalBase();
        this.report = this.rawReport.map((department) => ({
            id: department.Id,
            name: department.Name,
            total: this.parseDurationTotals(department.WorkTotals),
            jobsByUsers: department.Persons?.map(this.parsePerson.bind(this)) || [],
            entityInfo: {
                type: EntityDataTypes.Instance,
                name: knownEntityTypes.Department,
                id: department.Id
            }
        }));
    }
    parsePeriodTotalBase() {
        const base = {};
        this.workDaysData.WorkDays.forEach((day) => {
            const date = dayjs(day.Date).format(WTS_MOMENT_SHORT_DATE_MASK);
            base[date] = '';
        });
        this.byDaysBase = base;
    }
    parseDurationTotals(totals, defaultValue = '') {
        const byDays = { ...this.byDaysBase };
        if (defaultValue !== '') {
            Object.keys(byDays).forEach((date) => {
                byDays[date] = defaultValue;
            });
        }
        Object.entries(totals.Spans || {}).forEach(([date, value]) => {
            const day = dayjs(date).format(WTS_MOMENT_SHORT_DATE_MASK);
            byDays[day] = String(Utils.time.timeSpanToHoursDecimal(value));
        });
        return ({
            total: String(Utils.time.timeSpanToHoursDecimal(totals.Total)),
            byDays
        });
    }
    parsePerson(person) {
        this.currentPersonContracts = new PersonContracts(person.Contracts || []);
        return ({
            user: this.getUser(person),
            overUnderTime: this.getOverUnderTime(person),
            planned: this.parseDurationTotals(person.PlannedTotals),
            hoursWorked: this.parseDurationTotals(person.WorkTotals),
            difference: this.getDifference(person),
            absences: this.getAbsences(person.AbsenceTotals, person.Absences),
            totalErrors: this.getTotalErrors(person.ErrorTotals.Total),
            errors: this.getByDayErrors(person.ErrorTotals),
            actions: this.getActions(person).concat(this.getWorkTimeSumTypes(person)),
            confirmations: this.getConfirmations(person.Confirmation, this.levels),
            FirstName: person.Firstname,
            LastName: person.Lastname,
            AbsenceTime: Utils.time.timeSpanWithDaysToUsual(person.AbsenceTotals.Total),
            AbsenceHours: Utils.time.timeSpanToHoursDecimal(person.AbsenceTotals.Total).toString(),
            OverUnderTime: Utils.time.timeSpanWithDaysToUsual(person.IntervalOverUnder),
            OverUnderHours: Utils.time.timeSpanToHoursDecimal(person.IntervalOverUnder).toString(),
            NormHours: Utils.time.timeSpanWithDaysToUsual(person.StandardHours),
            CalculatedLunches: person.Confirmation?.Lunches || false,
            CalculatedBreaks: person.Confirmation?.Breaks || false,
            CalculatedSlipTimes: person.Confirmation?.SlipTimes || false
        });
    }
    getUser(person) {
        const { Firstname, Lastname } = person;
        return {
            id: person.Id,
            name: `${Lastname}, ${Firstname}`.trim(),
            externalCode: person.ExternalCode || '',
            entityInfo: {
                type: EntityDataTypes.Instance,
                name: knownEntityTypes.User,
                id: person.Id.toString()
            }
        };
    }
    getOverUnderTime(person) {
        const byDays = { ...this.byDaysBase };
        Object.entries(person.PlannedWorkShifts?.Spans || {}).forEach(([date, value]) => {
            const day = dayjs(date).format(WTS_MOMENT_SHORT_DATE_MASK);
            byDays[day] = this.getWorkShiftsStr(value || []);
        });
        return {
            total: String(Utils.time.timeSpanToHoursDecimal(person.IntervalOverUnder)),
            byDays
        };
    }
    getWorkShiftsStr(workShifts) {
        let str = '';
        workShifts.forEach((shift, i) => {
            // cut the string to 2 characters, trim, and add a comma if it's not the first element
            str += `${i > 0 ? ',' : ''}${shift.trim().slice(0, 2).trim()} `;
        });
        return str;
    }
    getDifference(person) {
        const planned = this.parseDurationTotals(person.PlannedTotals, '0');
        const worked = this.parseDurationTotals(person.WorkTotals, '0');
        const absences = this.parseDurationTotals(person.AbsenceTotals, '0');
        const byDays = {};
        let grandTotal = 0;
        let showRow = false;
        Object.keys(planned.byDays).forEach((date) => {
            if (this.currentPersonContracts.isDailyOnDate(date))
                return;
            showRow = true;
            const dayWorked = parseFloat(worked.byDays[date]);
            const dayPlanned = parseFloat(planned.byDays[date]);
            const dayAbsences = parseFloat(absences.byDays[date]);
            if (dayPlanned || dayWorked) {
                const dayTotal = precisionRound(dayWorked - dayPlanned + dayAbsences, 2);
                byDays[date] = dayTotal.toString();
                grandTotal += dayTotal;
            }
        });
        const hoursByDays = {};
        Object.keys(byDays).forEach((date) => {
            hoursByDays[date] = Utils.time.decimalToTimeSpan(+(byDays[date] || 0));
        });
        return {
            total: precisionRound(grandTotal, 2).toString(),
            byDays,
            hoursByDays,
            showRow
        };
    }
    getAbsences(totals, details) {
        const byDays = { ...this.byDaysBase };
        Object.entries(details.Spans || {}).forEach(([date, absence]) => {
            const timeValue = totals.Spans?.[date] || '0.00:00:00';
            const day = dayjs(date).format(WTS_MOMENT_SHORT_DATE_MASK);
            byDays[day] = {
                Name: absence.Name || 'N/A',
                ShortName: absence.ShortName || absence.ExternalId || '',
                Time: Utils.time.timeSpanWithDaysToUsual(timeValue),
                Hours: String(Utils.time.timeSpanToHoursDecimal(timeValue))
            };
        });
        return ({
            total: String(Utils.time.timeSpanToHoursDecimal(totals.Total)),
            byDays
        });
    }
    getByDayErrors(errors) {
        const errorsMap = {};
        Object.entries(errors.Spans || {}).forEach(([date, value]) => {
            const day = dayjs(date).format(WTS_MOMENT_SHORT_DATE_MASK);
            errorsMap[day] = value || null;
        });
        return errorsMap;
    }
    getTotalErrors(error) {
        return error?.length ? error : null;
    }
    getActions(person) {
        const candidate = person.Jobs.map((job) => ({
            name: job.Name,
            externalCode: job.ExternalId || null,
            profession: job.Profession?.Name || null,
            entityInfo: {
                type: EntityDataTypes.Instance,
                name: knownEntityTypes.Job,
                id: job.Id.toString()
            },
            ...this.parseDurationTotals(job.WorkTotals),
            byType: job.WorkTimeTypes.map((type) => ({
                type: type.Name,
                externalCode: type.ExternalId,
                entityInfo: {
                    type: EntityDataTypes.Instance,
                    name: knownEntityTypes.WorkTimeCalculationType,
                    id: type.Id.toString()
                },
                ...this.parseDurationTotals(type.Totals)
            }))
        }));
        candidate.push(...this.getOvertimeCompensationRows(person));
        return candidate;
    }
    getWorkTimeSumTypes(person) {
        const candidate = [];
        person.WorkTimeSumTypes?.forEach((type) => {
            if (type.Total === '00:00:00')
                return;
            candidate.push({
                name: type.Name,
                externalCode: type.ExternalId || null,
                profession: null,
                entityInfo: {
                    type: EntityDataTypes.Instance,
                    name: knownEntityTypes.WorkTimeCalculationType,
                    id: type.Id.toString()
                },
                ...this.parseDurationTotals({
                    Total: type.Total,
                    Spans: {}
                }),
                byType: []
            });
        });
        return candidate;
    }
    getOvertimeCompensationRows(person) {
        const rowsToAdd = [];
        const displayedPeriodStart = dayjs(this.workDaysData.Period.StartDate);
        const displayedPeriodEnd = Utils.periods.endDateInclusiveToExclusive(dayjs(this.workDaysData.Period.EndDate));
        const isSummedPeriodEnd = person.CalculationIntervalEnd
            && dayjs(person.CalculationIntervalEnd).isBetween(displayedPeriodStart, displayedPeriodEnd, 'd', '[]');
        const hasOverHours = person.RunningOverUnder !== '00:00:00' && !person.RunningOverUnder?.startsWith('-');
        if (isSummedPeriodEnd && hasOverHours) {
            const base = {
                externalCode: null,
                profession: null,
                byDays: {},
                byType: []
            };
            const total = Utils.time.timeSpanToHoursDecimal(person.RunningOverUnder);
            const used = Utils.time.timeSpanToHoursDecimal(person.FreeTime);
            const left = total - used;
            if (used > 0) {
                rowsToAdd.push({
                    ...base,
                    name: I18n.t('CompensateWithFreeTime'),
                    total: (-used).toString(),
                    entityInfo: {
                        type: EntityDataTypes.Instance,
                        name: knownEntityTypes.FreeTime,
                        id: person.Id.toString()
                    }
                });
            }
            rowsToAdd.push({
                ...base,
                name: I18n.t('OverHours'),
                total: left.toString()
            });
        }
        return rowsToAdd;
    }
    getConfirmations(confirmation, totalLevels) {
        const candidate = new Array(totalLevels).fill(undefined);
        confirmation?.Levels?.forEach((conf) => {
            candidate[conf.Level - 1] = this.getSingleConfirmation(conf);
        });
        return candidate;
    }
    getSingleConfirmation(confirmation) {
        const confirmedBy = `${confirmation.Confirmer.Lastname}, ${confirmation.Confirmer.Firstname}`;
        return ({
            date: dayjs(confirmation.CreatedAt).format(MOMENT_DATE_TIME_MASK),
            externalCode: confirmation.Confirmer.ExternalCode,
            id: confirmation.Id,
            level: confirmation.Level,
            title: confirmedBy
        });
    }
}
